import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import { deleteBatch } from '@/services/subtitleTranslation';
import UserStore from '@/store/User';
import { ModelTypeEnum, ModelTypeNameMap } from '@/utils/const';
import { F_CLOUD_TASK_TYPE } from '@/utils/const';
import { t } from '@/utils/helper/intl';
import { faceSwapperURL, getFeatureName } from '@/utils/router';

import {
  type AssetItem,
  type CreationDetail,
  fetchTaskList,
  type QueryType,
  type TaskItem,
  TaskType,
} from './service';

export enum MediaType {
  VIDEO = 3,
  IMAGE = 2,
  AUDIO = 5,
  PENDING = 0,
}

export enum TaskState {
  PENDING = 1,
  SUCCESS = 2,
  FAILED = 3,
  NonCompliance = 5,
}

export interface MediaItem {
  id: number;
  title: string;
  url: string;
  cover: string;
  duration?: number;
  state: TaskState;
  mediaType: MediaType;
  rawData: AssetItem | TaskItem;
}

export interface CombinedTaskItem {
  id: number;
  taskId?: number;
  title: string;
  tags?: string[];
  createTime: number;
  cloudTaskType: F_CLOUD_TASK_TYPE;
  state: TaskState;
  mediaItems: MediaItem[];
  prompt?: string;
  promptImage?: string;
  rawData: CreationDetail;
  modelName?: string;
}

interface UseTaskListOptions {
  queryTypes: QueryType[];
  pageSize?: number;
  batchPollingInterval?: number;
  pathname?: string; // 用于格式化 title
}

export const DEFAULT_PAGE_SIZE = 40;
const DEFAULT_BATCH_POLLING_INTERVAL = 15000; // 15 seconds
const PAGE_SWITCH_DEBOUNCE = 300;

const getParamsByModelType = (extendJson: string, defaultText: string) => {
  const result = { title: defaultText, tags: [], modelName: '' };
  if (!extendJson) return result;
  try {
    const taskExtraParam = JSON.parse(JSON.parse(extendJson)?.taskExtraParam);
    const { modelType, modelName } = taskExtraParam;
    result.title = ModelTypeNameMap[modelType as ModelTypeEnum] || defaultText;
    result.modelName = modelName;
    return result;
  } catch {
    return result;
  }
};

// 内部的 title 格式化函数
function formatTitleField(detail: CreationDetail, pathname?: string) {
  let title = '';
  let tags = [];
  let modelName = '';

  const { cloudTaskType, extendJson } = detail;

  if (pathname === faceSwapperURL) {
    title = getFeatureName(pathname);
    if (cloudTaskType === F_CLOUD_TASK_TYPE.F_HEAD_SWAP) {
      tags.push('v2');
    } else {
      tags.push('v1');
    }
  } else if (
    [F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE, F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE].includes(
      cloudTaskType!,
    )
  ) {
    return getParamsByModelType(
      extendJson!,
      cloudTaskType === F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE
        ? t('9a1641a16a4cc4df9ffa2a17b6348ae0')
        : t('c7741980e9f14c014b9e2182050da31e'),
    );
  } else if (F_CLOUD_TASK_TYPE.F_VIDEO_EDIT === cloudTaskType) {
    const result = getParamsByModelType(
      extendJson!,
      t('9613af8a82f375ffce54844fc0a470cc'),
    );
    // 兼容老作品数据
    !result.modelName && (result.modelName = 'Kling Video O1 Std Video Edit');
    return result;
  } else if (cloudTaskType === F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH) {
    title = t('merror.sub.tts');
  } else if (cloudTaskType === F_CLOUD_TASK_TYPE.F_VOICE_CLONE) {
    title = t('ce24a821ce9a84f071a4b291570d64b6');
  } else if (pathname) {
    title = getFeatureName(pathname);
  }

  return {
    title,
    tags,
    modelName,
  };
}

export const useTaskList = ({
  queryTypes,
  pageSize = DEFAULT_PAGE_SIZE,
  batchPollingInterval = DEFAULT_BATCH_POLLING_INTERVAL,
  pathname,
}: UseTaskListOptions) => {
  const [taskList, setTaskList] = useState<CombinedTaskItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [visiblePages, setVisiblePagesState] = useState<Set<number>>(new Set([1]));

  const pollingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const loadingRef = useRef(false);
  const visiblePagesRef = useRef<Set<number>>(new Set([1]));
  const taskListRef = useRef<CombinedTaskItem[]>([]);

  // 同步 taskList 到 ref
  taskListRef.current = taskList;

  // 封装 setVisiblePages：同时更新 state 和 ref
  const setVisiblePages = useCallback((pages: Set<number>) => {
    visiblePagesRef.current = pages;
    setVisiblePagesState(pages);
  }, []);

  const mapAssetsToMediaItems = useCallback((assets: AssetItem[] = []): MediaItem[] => {
    return assets.map((asset) => ({
      id: asset.id,
      title: asset.title ?? (asset.extendInfo?.videoTitle || ''),
      url: asset.fileUrl,
      cover: asset.cover,
      duration: asset.duration,
      state: TaskState.SUCCESS,
      mediaType: asset.type,
      rawData: asset,
    }));
  }, []);

  const mapTasksToMediaItems = useCallback((tasks: TaskItem[] = []): MediaItem[] => {
    return tasks.map((task) => ({
      id: task.id,
      title: task.videoTitle,
      url: task.videoUrl,
      cover: task.videoPreviewUrl,
      state: task.state,
      mediaType: task.taskType,
      rawData: task,
    }));
  }, []);

  const pickPromptFromDetail = useCallback(
    (detail: CreationDetail, type: F_CLOUD_TASK_TYPE) => {
      const { assetList = [], taskList = [] } = detail;
      const sourceTask = [...assetList, ...taskList].find((task) => {
        try {
          !task.extendInfo && (task.extendInfo = {});
          const { taskExtraParam } = task.extendInfo;
          if (
            [
              F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE,
              F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE,
              F_CLOUD_TASK_TYPE.F_VIDEO_EDIT,
            ].includes(type)
          ) {
            if (!taskExtraParam) return false;
            const params = JSON.parse(JSON.parse(taskExtraParam)?.params);
            // image images 对应视频、图片生成的两个功能对应的取值模式
            // 其他功能取图片用 videoPreviewUrl
            const { prompt, image, images } = params;
            if (prompt || image || images?.length) {
              task.extendInfo.prompt = prompt;
              task.extendInfo.promptImage = images || image;
              return true;
            }
            return false;
          }
          return false;
        } catch {
          return false;
        }
      });

      return {
        prompt: sourceTask?.extendInfo?.prompt,
        promptImage: sourceTask?.extendInfo?.promptImage,
      };
    },
    [],
  );

  const getBatchState = useCallback((detail: CreationDetail): number => {
    const tasks = detail.taskList || [];

    // 如果 taskList 为空，根据 assetList 判断状态
    if (tasks.length === 0) {
      return detail.assetList && detail.assetList.length > 0
        ? TaskState.SUCCESS
        : TaskState.SUCCESS; // 空批次视为已完成，不需要轮询
    }

    // 检查是否有 pending 状态的任务（state = 1）
    if (tasks.some((task) => task.state === TaskState.PENDING)) {
      return TaskState.PENDING;
    }

    // 检查是否有失败的任务（state >= 3）
    if (tasks.some((task) => task.state >= TaskState.FAILED)) {
      return TaskState.FAILED;
    }

    return TaskState.SUCCESS;
  }, []);

  const transformDetailToCombinedItem = useCallback(
    (detail: CreationDetail, fallbackId: string | number): CombinedTaskItem => {
      const firstTask = detail.taskList?.[0];
      const firstAsset = detail.assetList?.[0];
      const cloudTaskType =
        detail.cloudTaskType ?? firstTask?.cloudTaskType ?? firstAsset?.cloudTaskType;

      const mediaItems = [
        ...mapTasksToMediaItems(detail.taskList || []),
        ...mapAssetsToMediaItems(detail.assetList || []),
      ];
      const { prompt, promptImage } = pickPromptFromDetail(detail, cloudTaskType);
      const taskId = detail.taskId;
      const { title, tags, modelName } = formatTitleField(detail, pathname);
      const createTime = detail.createTime ?? Date.now();
      const id = detail.taskId;

      return {
        id,
        taskId,
        title,
        tags,
        createTime,
        cloudTaskType,
        state: getBatchState(detail),
        mediaItems,
        prompt,
        promptImage,
        modelName,
        rawData: detail,
      };
    },
    [
      mapTasksToMediaItems,
      mapAssetsToMediaItems,
      pickPromptFromDetail,
      getBatchState,
      pathname,
    ],
  );

  const clearAllPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = undefined;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
  }, []);

  // 轮询所有可见页面（使用 ref 避免依赖 taskList）
  const pollVisiblePagesRef = useRef<() => Promise<void>>();

  pollVisiblePagesRef.current = async () => {
    // 收集需要轮询的页码（只轮询有 pending 任务的页面）
    const pagesToPoll: number[] = [];

    visiblePagesRef.current.forEach((page) => {
      // 检查该页面是否有 pending 任务（使用 ref 获取最新的 taskList）
      const startIndex = (page - 1) * pageSize;
      const endIndex = page * pageSize;
      const pageTasks = taskListRef.current.slice(startIndex, endIndex);

      const hasPending = pageTasks.some((task) => {
        const taskListFromRaw = task.rawData?.taskList || [];
        return taskListFromRaw.some((t) => t.state === TaskState.PENDING);
      });

      if (hasPending) {
        pagesToPoll.push(page);
      }
    });

    // 如果没有需要轮询的页面，停止轮询
    if (pagesToPoll.length === 0) {
      return;
    }

    try {
      // 并发请求所有需要轮询的页面
      const responses = await Promise.all(
        pagesToPoll.map((page) =>
          fetchTaskList({
            queryTypes,
            pageNum: page,
            pageSize,
          }),
        ),
      );

      // 收集所有新数据
      const allNewTasks: CombinedTaskItem[] = [];
      responses.forEach((response, index) => {
        if (response.success && response.data) {
          const detailList = response.data.creationDetailList || [];
          const page = pagesToPoll[index];

          detailList.forEach((detail, detailIndex) => {
            const combinedItem = transformDetailToCombinedItem(
              detail,
              (page - 1) * pageSize + detailIndex,
            );
            allNewTasks.push(combinedItem);
          });
        }
      });

      // 智能合并：按 taskId 更新，保留本地状态（如用户已删除的 mediaItem）
      setTaskList((prev) => {
        const newTaskMap = new Map(allNewTasks.map((t) => [t.taskId, t]));

        const updated = prev.map((oldTask) => {
          const newTask = newTaskMap.get(oldTask.taskId);
          if (newTask) {
            // 合并策略：更新 state 和 mediaItems，但保留其他本地状态
            return {
              ...oldTask,
              state: newTask.state,
              mediaItems: newTask.mediaItems,
              rawData: newTask.rawData,
            };
          }
          return oldTask;
        });

        return updated;
      });

      // 检查新数据中是否有 pending 任务（直接检查 allNewTasks，不依赖 setTaskList 回调）
      const hasAnyPending = allNewTasks.some((task) => {
        const taskListFromRaw = task.rawData?.taskList || [];
        return taskListFromRaw.some((t) => t.state === TaskState.PENDING);
      });

      // 只在还有 pending 任务时继续轮询
      if (hasAnyPending) {
        // 先清除旧定时器，避免定时器累积
        if (pollingTimerRef.current) {
          clearTimeout(pollingTimerRef.current);
        }
        pollingTimerRef.current = setTimeout(() => {
          pollVisiblePagesRef.current?.();
        }, batchPollingInterval);
      }
    } catch (error) {
      console.error('[TaskList] Error polling visible pages:', error);
      // 出错后重试（因为不确定服务器状态，继续尝试获取最新数据）
      // 先清除旧定时器，避免定时器累积
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
      pollingTimerRef.current = setTimeout(() => {
        pollVisiblePagesRef.current?.();
      }, batchPollingInterval);
    }
  };

  // 启动轮询（不立即执行，等待 interval 后再第一次轮询）
  const startPolling = useCallback(() => {
    clearAllPolling();
    // 启动定时器，等待 batchPollingInterval 后再第一次轮询
    pollingTimerRef.current = setTimeout(() => {
      pollVisiblePagesRef.current?.();
    }, batchPollingInterval);
  }, [clearAllPolling, batchPollingInterval]);

  const loadTaskList = useCallback(
    async (page: number, append: boolean = false) => {
      try {
        if (!queryTypes.length || loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);

        const response = await fetchTaskList({
          queryTypes,
          pageNum: page,
          pageSize,
        });

        if (response.success && response.data) {
          const detailList = response.data.creationDetailList || [];

          const combinedList: CombinedTaskItem[] = detailList.map((detail, index) =>
            transformDetailToCombinedItem(detail, (page - 1) * pageSize + index),
          );

          combinedList.sort((a, b) => {
            const timeA =
              typeof a.createTime === 'string'
                ? new Date(a.createTime).getTime()
                : a.createTime;
            const timeB =
              typeof b.createTime === 'string'
                ? new Date(b.createTime).getTime()
                : b.createTime;
            return timeB - timeA;
          });

          let newTaskList: CombinedTaskItem[];
          if (append) {
            setTaskList((prev) => {
              newTaskList = [...prev, ...combinedList];
              return newTaskList;
            });
          } else {
            newTaskList = combinedList;
            setTaskList(newTaskList);
          }

          setHasMore(detailList.length === pageSize);
        } else {
          // API 返回错误（如 success: false），停止加载更多
          setHasMore(false);
          // 只在非追加模式下清空列表
          if (!append) {
            setTaskList([]);
          }
        }
      } catch (error) {
        console.error('Failed to load task list:', error);
        // 网络错误或异常，也要停止加载更多，避免无限重试
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [queryTypes, pageSize, transformDetailToCombinedItem],
  );

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const nextPage = pageNum + 1;
    setPageNum(nextPage);
    loadTaskList(nextPage, true);
  }, [loading, hasMore, pageNum, loadTaskList]);

  const refresh = useCallback(() => {
    try {
      if (loadingRef.current) {
        return;
      }

      // 检查登录状态
      const isLoggedIn = UserStore.getState().getLoginState();
      if (!isLoggedIn) {
        return;
      }

      if (!queryTypes.length) {
        return;
      }
    } finally {
      setLoading(false);
    }

    clearAllPolling();
    setTaskList([]);
    setPageNum(1);
    setHasMore(true);
    setVisiblePages(new Set([1])); // 重置为第一页

    // 立即重新加载
    loadTaskList(1, false);
  }, [clearAllPolling, loadTaskList, queryTypes.length]);

  const deleteTask = useCallback(async (taskId: number | string) => {
    try {
      const res = await deleteBatch({ parentId: taskId });
      if (res.code === 200) {
        message.success(t('merror.tip.deleteSuccess'));
        setTaskList((prev) => prev.filter((t) => t.id !== taskId));
      } else {
        message.error(res.message || t('merror.tip.deleteError'));
        return;
      }
    } catch (error) {
      message.error(t('merror.tip.deleteError'));
    }
  }, []);

  // 删除批次中的单个 mediaItem
  const deleteMediaItem = useCallback(
    (batchId: number | string, mediaItemId: number | string) => {
      setTaskList(
        (prev) =>
          prev
            .map((task) => {
              if (task.id === batchId) {
                // 过滤掉被删除的 mediaItem
                const updatedMediaItems = task.mediaItems.filter(
                  (item) => item.id !== mediaItemId,
                );

                // 如果删除后没有 mediaItem 了，删除整个批次
                if (updatedMediaItems.length === 0) {
                  return null;
                }

                return {
                  ...task,
                  mediaItems: updatedMediaItems,
                };
              }
              return task;
            })
            .filter(Boolean) as CombinedTaskItem[],
      );
    },
    [],
  );

  // 更新批次中单个 mediaItem 的 title
  const updateMediaItemTitle = useCallback(
    (batchId: number | string, mediaItemId: number | string, newTitle: string) => {
      setTaskList((prev) =>
        prev.map((task) => {
          if (task.id === batchId) {
            return {
              ...task,
              mediaItems: task.mediaItems.map((item) =>
                item.id === mediaItemId ? { ...item, title: newTitle } : item,
              ),
            };
          }
          return task;
        }),
      );
    },
    [],
  );

  // 当可见页面变化时，重新启动轮询（带 debounce）
  useEffect(() => {
    // 清除旧的 debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 300ms debounce：避免快速滚动时频繁切换轮询
    debounceTimerRef.current = setTimeout(() => {
      clearAllPolling();
      startPolling();
    }, PAGE_SWITCH_DEBOUNCE);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [visiblePages, clearAllPolling, startPolling]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return () => {
      clearAllPolling();
    };
  }, [clearAllPolling]);

  return {
    taskList,
    loading,
    hasMore,
    pageNum,
    loadMore,
    refresh,
    deleteTask,
    deleteMediaItem,
    updateMediaItemTitle,
    setVisiblePages,
  };
};
